import { presentColumn } from "./screenerPresentation";
import type {
  EnumPredicate,
  FlagPredicate,
  NumericPredicate,
  ScreenFilters,
  ScreenerColumnDef,
  ScreenerMeta,
  ScreenerPredicate,
  ScreenerPreset,
} from "./types";

export const EMPTY_FILTERS: ScreenFilters = { dslVersion: 1, predicates: [] };

export const LONDON_EXCHANGES = ["LSE"];

const isNumeric = (
  predicate: ScreenerPredicate,
): predicate is NumericPredicate => predicate.kind === "number";
const isEnum = (predicate: ScreenerPredicate): predicate is EnumPredicate =>
  predicate.kind === "enum";
const isFlag = (predicate: ScreenerPredicate): predicate is FlagPredicate =>
  predicate.kind === "flag";

const finite = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

/** Drops malformed predicates and orders the rest so equal screens key equally. */
export const canonicalFilters = (
  filters: ScreenFilters,
  sort = true,
): ScreenFilters => {
  const predicates = filters.predicates.filter((predicate) => {
    if (isNumeric(predicate)) {
      return predicate.op === "between"
        ? finite(predicate.value[0]) && finite(predicate.value[1])
        : finite(predicate.value);
    }
    if (isEnum(predicate)) return predicate.values.length > 0;
    return isFlag(predicate);
  });
  const sorted = [...predicates].sort((a, b) => {
    const left = `${a.column}|${a.kind}|${"op" in a ? a.op : ""}|${JSON.stringify(a)}`;
    const right = `${b.column}|${b.kind}|${"op" in b ? b.op : ""}|${JSON.stringify(b)}`;
    return left < right ? -1 : left > right ? 1 : 0;
  });
  return { dslVersion: 1, predicates: sort ? sorted : predicates };
};

export const filtersKey = (filters: ScreenFilters): string =>
  JSON.stringify(canonicalFilters(filters));

export const presetFilters = (
  meta: Pick<ScreenerMeta, "defaultPredicates">,
  preset: ScreenerPreset,
): ScreenFilters => ({
  dslVersion: 1,
  predicates: [...meta.defaultPredicates, ...preset.predicates],
});

// One predicate as the server's run hash sees it (shared filterDsl
// predicateSortKey): every field, whatever the object's key order.
const presetPredicateKey = (predicate: ScreenerPredicate): string => {
  if (isNumeric(predicate))
    return `number|${predicate.column}|${predicate.op}|${JSON.stringify(predicate.value)}`;
  if (isEnum(predicate))
    return `enum|${predicate.column}|${predicate.op}|${JSON.stringify(predicate.values)}`;
  return `flag|${predicate.column}|${predicate.value}`;
};

const presetSetKey = (filters: ScreenFilters): string =>
  filters.predicates.map(presetPredicateKey).sort().join("\n");

/**
 * The preset whose own filters (the default chips included) these are, in
 * any order and otherwise unchanged; null for a screen of the member's own.
 * The server's rule for a free preset run (allowanceService isPresetHash),
 * so Basic is never told a preset spends a session, or a changed one that it
 * doesn't. A preset id alone proves nothing.
 */
export const matchingPreset = (
  filters: ScreenFilters,
  meta: Pick<ScreenerMeta, "defaultPredicates" | "presets">,
): ScreenerPreset | null => {
  const key = presetSetKey(filters);
  return (
    meta.presets.find(
      (preset) => presetSetKey(presetFilters(meta, preset)) === key,
    ) ?? null
  );
};

export const defaultFilters = (
  meta: Pick<ScreenerMeta, "defaultPredicates">,
): ScreenFilters => ({
  dslVersion: 1,
  predicates: [...meta.defaultPredicates],
});

export interface NumericRange {
  lo?: number;
  hi?: number;
}

/** The effective range a column's numeric predicates express. */
export const numericRange = (
  filters: ScreenFilters,
  column: string,
): NumericRange => {
  const range: NumericRange = {};
  for (const predicate of filters.predicates) {
    if (!isNumeric(predicate) || predicate.column !== column) continue;
    if (predicate.op === "between") {
      range.lo = predicate.value[0];
      range.hi = predicate.value[1];
    } else if (predicate.op === "gte" || predicate.op === "gt")
      range.lo = predicate.value;
    else range.hi = predicate.value;
  }
  return range;
};

/** Replaces a column's numeric predicates with one that expresses the range; an empty range clears it. */
export const setNumericRange = (
  filters: ScreenFilters,
  column: string,
  range: NumericRange,
): ScreenFilters => {
  const others = filters.predicates.filter(
    (predicate) => !(isNumeric(predicate) && predicate.column === column),
  );
  const lo = finite(range.lo) ? range.lo : undefined;
  const hi = finite(range.hi) ? range.hi : undefined;
  let next: NumericPredicate | null = null;
  if (lo !== undefined && hi !== undefined)
    next = { kind: "number", column, op: "between", value: [lo, hi] };
  else if (lo !== undefined)
    next = { kind: "number", column, op: "gte", value: lo };
  else if (hi !== undefined)
    next = { kind: "number", column, op: "lte", value: hi };
  return { dslVersion: 1, predicates: next ? [...others, next] : others };
};

export const flagValue = (
  filters: ScreenFilters,
  column: string,
): boolean | undefined =>
  filters.predicates.find(
    (predicate): predicate is FlagPredicate =>
      isFlag(predicate) && predicate.column === column,
  )?.value;

export const setFlag = (
  filters: ScreenFilters,
  column: string,
  value: boolean | undefined,
): ScreenFilters => {
  const others = filters.predicates.filter(
    (predicate) => !(isFlag(predicate) && predicate.column === column),
  );
  return {
    dslVersion: 1,
    predicates:
      value === undefined
        ? others
        : [...others, { kind: "flag", column, value }],
  };
};

export const enumValues = (
  filters: ScreenFilters,
  column: string,
): string[] | undefined =>
  filters.predicates.find(
    (predicate): predicate is EnumPredicate =>
      isEnum(predicate) && predicate.column === column && predicate.op === "in",
  )?.values;

export const setEnumValues = (
  filters: ScreenFilters,
  column: string,
  values: string[] | undefined,
): ScreenFilters => {
  const others = filters.predicates.filter(
    (predicate) => !(isEnum(predicate) && predicate.column === column),
  );
  return {
    dslVersion: 1,
    predicates:
      values === undefined || values.length === 0
        ? others
        : [...others, { kind: "enum", column, op: "in", values }],
  };
};

export const removePredicateAt = (
  filters: ScreenFilters,
  index: number,
): ScreenFilters => ({
  dslVersion: 1,
  predicates: filters.predicates.filter((_, at) => at !== index),
});

// The predicate a loosen suggestion names (`${index}:${column}[:op]` over the
// filters as sent), removed only when it is that predicate: otherwise the
// first one with the same column and bound, or nothing, so a stale suggestion
// never drops a different constraint.
export const dropSuggestedPredicate = (
  filters: ScreenFilters,
  predicateId: string,
): ScreenFilters | null => {
  const [position, column, op] = predicateId.split(":");
  const named = (predicate: ScreenerPredicate | undefined): boolean =>
    predicate !== undefined &&
    predicate.column === column &&
    (op === undefined || ("op" in predicate && predicate.op === op));
  const index = Number(position);
  const at =
    Number.isInteger(index) && named(filters.predicates[index])
      ? index
      : filters.predicates.findIndex(named);
  if (at < 0) return null;
  return removePredicateAt(filters, at);
};

export const isLondonOnly = (filters: ScreenFilters): boolean => {
  const values = enumValues(filters, "exchange");
  return (
    values !== undefined &&
    values.length === LONDON_EXCHANGES.length &&
    values.every((value) => LONDON_EXCHANGES.includes(value))
  );
};

export const setLondonOnly = (
  filters: ScreenFilters,
  on: boolean,
  meta: Pick<ScreenerMeta, "defaultPredicates">,
): ScreenFilters => {
  if (on) return setEnumValues(filters, "exchange", [...LONDON_EXCHANGES]);
  const majors = meta.defaultPredicates.find(
    (predicate): predicate is EnumPredicate =>
      isEnum(predicate) && predicate.column === "exchange",
  );
  return setEnumValues(
    filters,
    "exchange",
    majors ? [...majors.values] : undefined,
  );
};

/** How many predicates are on a column group, for the "2 active" detail on a metric group row. */
export const activeCountFor = (
  filters: ScreenFilters,
  columnIds: readonly string[],
): number =>
  filters.predicates.filter((predicate) => columnIds.includes(predicate.column))
    .length;

/** True when the predicate is one of the meta's default chips (same column, kind and value). */
export const isDefaultPredicate = (
  predicate: ScreenerPredicate,
  meta: Pick<ScreenerMeta, "defaultPredicates">,
): boolean =>
  meta.defaultPredicates.some(
    (candidate) => JSON.stringify(candidate) === JSON.stringify(predicate),
  );

const DEFAULT_CHIP_LABELS: Record<string, string> = {
  "exchange|enum": "Major exchanges",
  "is_preferred_listing|flag": "Primary listing",
  "is_otc|flag": "OTC off",
};

export const defaultChipLabel = (
  predicate: ScreenerPredicate,
): string | undefined =>
  DEFAULT_CHIP_LABELS[`${predicate.column}|${predicate.kind}`];

// UTF-8 to a binary string without TextEncoder, which jsdom lacks.
const utf8ToBinary = (text: string): string =>
  encodeURIComponent(text).replace(/%([0-9A-F]{2})/g, (_, hex: string) =>
    String.fromCharCode(parseInt(hex, 16)),
  );

const binaryToUtf8 = (binary: string): string =>
  decodeURIComponent(
    Array.from(
      binary,
      (char) => `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`,
    ).join(""),
  );

export const encodeFiltersParam = (filters: ScreenFilters): string => {
  const json = JSON.stringify(canonicalFilters(filters, false));
  return btoa(utf8ToBinary(json))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
};

export const decodeFiltersParam = (
  param: string | null | undefined,
): ScreenFilters | null => {
  if (!param) return null;
  try {
    const base64 = param.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const parsed = JSON.parse(binaryToUtf8(atob(padded))) as unknown;
    if (!parsed || typeof parsed !== "object") return null;
    const candidate = parsed as { dslVersion?: unknown; predicates?: unknown };
    if (candidate.dslVersion !== 1 || !Array.isArray(candidate.predicates))
      return null;
    return canonicalFilters(
      {
        dslVersion: 1,
        predicates: candidate.predicates as ScreenerPredicate[],
      },
      false,
    );
  } catch {
    return null;
  }
};

export const columnById = (
  columns: readonly ScreenerColumnDef[],
): Map<string, ScreenerColumnDef> =>
  new Map(columns.map((column) => [column.id, presentColumn(column)]));

/** Columns a preset or filter set implies for the table when the user has not chosen a preset column set. */
export const impliedColumns = (
  filters: ScreenFilters,
  columns: Map<string, ScreenerColumnDef>,
  fallback: readonly string[],
): string[] => {
  const fromFilters = filters.predicates
    .map((predicate) => predicate.column)
    .filter((column) => columns.get(column)?.kind === "number");
  return Array.from(new Set([...fromFilters, ...fallback])).slice(0, 12);
};
